// ISDP CUSTOM: DevHub's custom "General" settings panel (ODX-styled), replacing
// the stock Backstage General tab. Trimmed per stakeholder feedback:
//   - Theme: LIGHT / DARK only (stock "Auto" removed — it flickered back to light).
//   - Language: EN / DE (drives the same appLanguageApiRef as the top-nav switcher).
//   - Pin Sidebar: removed entirely.
//   - Profile: shows the Okta profile picture (with initials/icon fallback).
//   - Identity: user + groups as PLAIN TEXT — no catalog links (User/Group kinds
//     are excluded from the catalog view, so the stock links dead-ended in 404s).
import { useEffect, useState } from 'react';
import { appThemeApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { appLanguageApiRef, useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { commonMessages } from '../../i18n/common';
import { ProfileAvatar } from './ProfileAvatar';
import { useProfileInfo } from './useProfileInfo';
import './DevHubSettings.css';

// "user:default/jane.doe" -> "jane.doe"; "group:default/admins" -> "admins".
function refName(ref: string): string {
  const afterKind = ref.includes(':') ? ref.slice(ref.indexOf(':') + 1) : ref;
  return afterKind.includes('/') ? afterKind.slice(afterKind.indexOf('/') + 1) : afterKind;
}

function ProfileCard() {
  const { t } = useTranslationRef(commonMessages);
  const { displayName, email, loading } = useProfileInfo();

  return (
    <section className="devhub-settings__card devhub-settings__profile">
      <h2 className="devhub-settings__card-title">{t('settings.profile')}</h2>
      <div className="devhub-settings__profile-body">
        <ProfileAvatar size={72} />
        <div className="devhub-settings__profile-meta">
          <span className="devhub-settings__profile-name">
            {displayName || (loading ? t('actions.loading') : t('settings.guest'))}
          </span>
          {email && <span className="devhub-settings__profile-email">{email}</span>}
        </div>
      </div>
    </section>
  );
}

function ThemeToggle() {
  const appThemeApi = useApi(appThemeApiRef);
  const { t } = useTranslationRef(commonMessages);
  const [isDark, setIsDark] = useState(
    () => appThemeApi.getActiveThemeId() === 'draeger-dark',
  );

  useEffect(() => {
    const sub = appThemeApi
      .activeThemeId$()
      .subscribe(id => setIsDark(id === 'draeger-dark'));
    return () => sub.unsubscribe();
  }, [appThemeApi]);

  // Only ever light/dark — never undefined ("auto") — so users can't land back on
  // the flicker-prone auto state.
  const set = (dark: boolean) =>
    appThemeApi.setActiveThemeId(dark ? 'draeger-dark' : 'draeger-light');

  return (
    <div
      className="devhub-settings__segmented"
      role="group"
      aria-label={t('settings.theme')}
    >
      <button
        type="button"
        className={`devhub-settings__seg ${!isDark ? 'is-active' : ''}`}
        onClick={() => set(false)}
        aria-pressed={!isDark}
      >
        <odx-icon className="odx-icon" name="core::brightness" size="sm" />
        {t('settings.light')}
      </button>
      <button
        type="button"
        className={`devhub-settings__seg ${isDark ? 'is-active' : ''}`}
        onClick={() => set(true)}
        aria-pressed={isDark}
      >
        <odx-icon className="odx-icon" name="core::night-mode" size="sm" />
        {t('settings.dark')}
      </button>
    </div>
  );
}

function LanguageToggle() {
  const languageApi = useApi(appLanguageApiRef);
  const { t } = useTranslationRef(commonMessages);
  const [lang, setLang] = useState(() => languageApi.getLanguage().language);

  useEffect(() => {
    const sub = languageApi.language$().subscribe(next => setLang(next.language));
    return () => sub.unsubscribe();
  }, [languageApi]);

  const isDe = lang === 'de';

  return (
    <div
      className="devhub-settings__segmented"
      role="group"
      aria-label={t('language.label')}
    >
      <button
        type="button"
        className={`devhub-settings__seg ${!isDe ? 'is-active' : ''}`}
        onClick={() => languageApi.setLanguage('en')}
        aria-pressed={!isDe}
      >
        EN
      </button>
      <button
        type="button"
        className={`devhub-settings__seg ${isDe ? 'is-active' : ''}`}
        onClick={() => languageApi.setLanguage('de')}
        aria-pressed={isDe}
      >
        DE
      </button>
    </div>
  );
}

function AppearanceCard() {
  const { t } = useTranslationRef(commonMessages);
  return (
    <section className="devhub-settings__card">
      <h2 className="devhub-settings__card-title">{t('settings.appearance')}</h2>
      <div className="devhub-settings__row">
        <div className="devhub-settings__row-text">
          <span className="devhub-settings__row-label">{t('settings.theme')}</span>
          <span className="devhub-settings__row-desc">{t('settings.themeDescription')}</span>
        </div>
        <ThemeToggle />
      </div>
      <div className="devhub-settings__row">
        <div className="devhub-settings__row-text">
          <span className="devhub-settings__row-label">{t('language.label')}</span>
          <span className="devhub-settings__row-desc">{t('settings.languageDescription')}</span>
        </div>
        <LanguageToggle />
      </div>
    </section>
  );
}

function IdentityCard() {
  const identityApi = useApi(identityApiRef);
  const { t } = useTranslationRef(commonMessages);
  const [userRef, setUserRef] = useState<string>();
  const [groups, setGroups] = useState<string[]>([]);

  useEffect(() => {
    let active = true;
    identityApi
      .getBackstageIdentity()
      .then(id => {
        if (!active) return;
        setUserRef(id.userEntityRef);
        setGroups(id.ownershipEntityRefs.filter(r => r.startsWith('group:')));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [identityApi]);

  return (
    <section className="devhub-settings__card">
      <h2 className="devhub-settings__card-title">{t('settings.identity')}</h2>
      {/* Plain, non-linked text — the stock card linked to catalog entity pages
          that 404 because User/Group kinds are hidden from the catalog view. */}
      <div className="devhub-settings__row">
        <span className="devhub-settings__row-label">{t('settings.user')}</span>
        <span className="devhub-settings__ident-value">
          {userRef ? refName(userRef) : '—'}
        </span>
      </div>
      <div className="devhub-settings__row">
        <span className="devhub-settings__row-label">{t('settings.groups')}</span>
        <span className="devhub-settings__ident-value">
          {groups.length > 0 ? (
            <span className="devhub-settings__chips">
              {groups.map(g => (
                <span className="devhub-settings__chip" key={g}>
                  {refName(g)}
                </span>
              ))}
            </span>
          ) : (
            t('settings.noGroups')
          )}
        </span>
      </div>
    </section>
  );
}

export const DevHubSettingsGeneral = () => {
  return (
    <div className="devhub-settings">
      <ProfileCard />
      <AppearanceCard />
      <IdentityCard />
    </div>
  );
};
